import { put, takeLatest } from "redux-saga/effects";
import { showToast } from "@/actions/appStatus.action";
import { getServerTimeAPI } from "@/api/auth.api";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import { GET_SERVER_TIME } from "@/constants/ActionTypes";
import { ONE_MINUTE_IN_MILI_SECONDS } from "@/constants/AppConstants";
import { IInitialState } from "@/types/initialState.types";
import apiErrorHandler from "@/utils/apiErrorHandler";
import { setLocalStorageItem } from "@/utils/localStorageHelper";
import {
  EErrorLevel,
  EErrorSource,
  EInfoSource,
  errorLogger,
  infoLogger,
} from "@/utils/ubaTracking.utils";
import {
  SERVER_TIME_API_ERROR_MESSAGE,
  SERVER_TIME_API_HTTP_STATUS_CODES,
} from "@/constants/ServerTimeConstants";
import { HTTP_STATUS_CODES } from "@/constants/HttpConstants";
import { IGetServerTime } from "@/types/serverTime.types";
import { call, select } from "./utils.saga";

export function* getServerTime() {
  try {
    const initialClientTime: string = new Date().toISOString();
    const requestTime: number = new Date().getTime();
    const { data, status } = yield* call(getServerTimeAPI);
    const serverTime: string = data.server_time;

    if (status === HTTP_STATUS_CODES.OK && serverTime) {
      const prevOffsetTime: number = yield* select(
        (state: IInitialState) => state.serverTime.offsetTime,
      );
      let offsetTime: number = new Date(serverTime).getTime() - Date.now();
      const responseTime: number = new Date().getTime();
      const processLatencyTime: number = responseTime - requestTime;
      offsetTime += processLatencyTime;
      const offsetDiff: number = prevOffsetTime - offsetTime;
      const isSystemTimeMismatch: boolean =
        Math.abs(offsetTime) > ONE_MINUTE_IN_MILI_SECONDS &&
        Math.abs(offsetDiff) > ONE_MINUTE_IN_MILI_SECONDS * 5;

      const payload: IGetServerTime = {
        initialClientTime,
        serverTime,
        offsetTime,
        isSystemTimeMismatch,
      };

      if (isSystemTimeMismatch) {
        infoLogger("server time mismatch", EInfoSource.SERVER_TIME, {
          ...payload,
        });
      }

      yield put({
        type: `${GET_SERVER_TIME}_SUCCESS`,
        payload,
      });

      setLocalStorageItem("serverTime", payload);
    } else {
      infoLogger(SERVER_TIME_API_ERROR_MESSAGE, EInfoSource.SERVER_TIME, {
        httpStatus: status,
        serverTime,
      });
    }
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.CRITICAL, EErrorSource.SERVER_TIME);
    const message = yield* call(
      apiErrorHandler,
      error,
      SERVER_TIME_API_HTTP_STATUS_CODES,
      SERVER_TIME_API_ERROR_MESSAGE,
    );
    if (message)
      yield put(
        showToast({
          message,
          type: EToastType.ERROR,
        }),
      );
  }
}

export default function* loadServerTimeSaga() {
  yield takeLatest(GET_SERVER_TIME, getServerTime);
}
